import { apiClient } from '@/lib/api/client';
import { reportAnalyticsError } from './analytics-error-handler';

export interface TrackEventPayload {
  event_type: string;
  event_category: string;
  event_data?: Record<string, unknown>;
  session_id?: string;
  character_id?: number;
  page_url?: string;
  referrer?: string;
  user_agent?: string;
  ip_address?: string;
  device_type?: string;
  browser?: string;
}

export interface SessionCreatePayload {
  session_id: string;
  device_type?: string;
  browser?: string;
  ip_address?: string;
  referrer?: string;
}

export interface SessionUpdatePayload {
  end_time?: string;
  duration_seconds?: number;
  page_views?: number;
  total_messages?: number;
  total_swipes?: number;
  total_purchases?: number;
  total_spent?: number;
  exit_page?: string;
  is_active?: boolean;
}

export interface SessionCreateResponse {
  success: boolean;
  session_id: number;
}

export interface SessionMutationResponse {
  success: boolean;
  message: string;
  session_found: boolean;
}

/**
 * Safe wrapper for analytics API calls that reports errors but doesn't throw
 */
async function safeAnalyticsCall<T>(
  operation: () => Promise<T>,
  errorType: string,
  context?: { eventType?: string; eventData?: Record<string, unknown> }
): Promise<T | null> {
  try {
    return await operation();
  } catch (error) {
    void reportAnalyticsError(errorType, error instanceof Error ? error : String(error), context);
    return null;
  }
}

export const analyticsAPI = {
  events: {
    track: (data: TrackEventPayload) =>
      safeAnalyticsCall(() => apiClient.post('/analytics/events/track', data), 'event_tracking_failed', {
        eventType: data.event_type,
        ...(data.event_data === undefined ? {} : { eventData: data.event_data }),
      }),
    trackBulk: (events: TrackEventPayload[], session_id?: string) =>
      safeAnalyticsCall(
        () => apiClient.post('/analytics/events/bulk', { events, session_id }),
        'bulk_event_tracking_failed',
        { eventType: 'bulk', eventData: { count: events.length } }
      ),
  },
  sessions: {
    create: (data: SessionCreatePayload) => apiClient.post<SessionCreateResponse>('/analytics/sessions/create', data),
    update: (sessionId: string, data: SessionUpdatePayload) =>
      apiClient.put<SessionMutationResponse>(`/analytics/sessions/${encodeURIComponent(sessionId)}/update`, data),
    end: (sessionId: string, endReason?: string) =>
      apiClient.post<SessionMutationResponse>(
        `/analytics/sessions/${encodeURIComponent(sessionId)}/end${endReason ? `?end_reason=${encodeURIComponent(endReason)}` : ''}`
      ),
    heartbeat: (sessionId: string) =>
      apiClient.post<SessionMutationResponse>(`/analytics/sessions/${encodeURIComponent(sessionId)}/heartbeat`),
  },
  // Export error reporting for direct use
  reportError: reportAnalyticsError,
};

export default analyticsAPI;

// =====================================================
// Preference Insights Types & API (Admin Only)
// =====================================================

export interface PreferenceScore {
  name: string;
  score: number;
  percentage: number;
  count: number;
}

export interface PlatformPreferenceInsights {
  period_days: number;
  top_genders: PreferenceScore[];
  top_age_ranges: PreferenceScore[];
  top_ethnicities: PreferenceScore[];
  top_body_types: PreferenceScore[];
  top_personality_tags: PreferenceScore[];
  avg_messages_per_user: number;
  avg_session_duration: number;
  peak_activity_hours: number[];
}

export interface OnboardingSelectionItem {
  attribute_key: string;
  attribute_value: string;
  selection_count: number;
  percentage: number;
}

export interface OnboardingInsights {
  period_days: number;
  total_completed_onboardings: number;
  total_skipped_onboardings: number;
  completion_rate: number; // Already in percentage (e.g., 75.0 means 75%)
  top_body_types: OnboardingSelectionItem[];
  top_hair_colors: OnboardingSelectionItem[];
  top_eye_colors: OnboardingSelectionItem[];
  top_ethnicities: OnboardingSelectionItem[];
  top_age_ranges: Array<{ range: string; count: number; percentage: number }>;
  top_selected_characters: Array<{ character_id: number; name: string; selections: number; percentage: number }>;
  generated_at: string;
}

export interface UserPreferenceProfile {
  user_id: number;
  analysis_period_days: number;
  preferred_genders: Record<string, number>;
  preferred_age_ranges: Record<string, number>;
  preferred_ethnicities: Record<string, number>;
  preferred_body_types: Record<string, number>;
  preferred_personality_tags: Record<string, number>;
  avg_messages_per_chat: number;
  avg_session_duration_seconds: number;
  total_interactions: number;
}

export interface AffinityInsights {
  period_days: number;
  generated_at: string;
  summary: {
    total_affinity_records: number;
    avg_affinity_score: number;
    avg_event_multiplier: number;
  };
  affinity_distribution: Array<{ range: string; count: number }>;
  multiplier_distribution: Array<{ range: string; count: number }>;
  top_characters: Array<{
    character_id: number;
    name: string;
    popularity_score: number;
    engagement_score: number;
    total_matches: number;
    swipe_right_rate: number;
  }>;
  swipe_by_body_type: Array<{
    body_type: string;
    swipe_right_rate: number;
    total_swipes: number;
  }>;
  match_to_message: {
    total_matches: number;
    matches_with_messages: number;
    conversion_rate: number;
  };
  profile_stats: {
    total_profiles: number;
    onboarding_completed: number;
    with_tracking_data: number;
    tracking_coverage: number;
  };
  task_status: {
    last_preference_aggregation: string | null;
    last_popularity_aggregation: string | null;
    last_affinity_calculation: string | null;
  };
}

export interface PerformanceArchetype {
  key: string;
  attributes: {
    gender: string;
    age_bucket: string;
    ethnicity: string;
    body_type: string;
    hair_color: string;
    eye_color: string;
  };
  character_ids: number[];
  character_count: number;
  exposed_users: number;
  exposure_events: number;
  like_rate_percent: number;
  chat_start_rate_percent: number;
  repeat_chatter_rate_percent: number;
  payer_rate_percent: number;
  attributed_revenue_minor_units: number;
  revenue_per_100_exposed_minor_units: number;
  confidence_adjusted_score: number;
  revenue_attribution: 'seven_day_last_character_touch';
}

export const preferenceInsightsApi = {
  /**
   * Get platform-wide preference insights (Admin only)
   */
  getPlatformInsights: async (days = 30): Promise<PlatformPreferenceInsights> => {
    const response = await apiClient.get<PlatformPreferenceInsights>('/analytics/preferences/platform', {
      params: { days },
    });
    return response.data;
  },

  /**
   * Get onboarding selection insights (Admin only)
   */
  getOnboardingInsights: async (days = 30): Promise<OnboardingInsights> => {
    const response = await apiClient.get<OnboardingInsights>('/analytics/preferences/onboarding', {
      params: { days },
    });
    return response.data;
  },

  /**
   * Get user preference profile (Admin only)
   */
  getUserPreferences: async (userId: number, days = 30): Promise<UserPreferenceProfile> => {
    const response = await apiClient.get<UserPreferenceProfile>(`/analytics/preferences/user/${userId}`, {
      params: { days },
    });
    return response.data;
  },

  /**
   * Get current user's own preference profile
   */
  getMyPreferences: async (days = 30): Promise<UserPreferenceProfile> => {
    const response = await apiClient.get<UserPreferenceProfile>('/analytics/preferences/me', {
      params: { days },
    });
    return response.data;
  },

  /**
   * Get affinity system insights (Admin only)
   */
  getAffinityInsights: async (days = 30): Promise<AffinityInsights> => {
    const response = await apiClient.get<AffinityInsights>('/analytics/preferences/affinity', {
      params: { days },
    });
    return response.data;
  },

  getPerformanceArchetypes: async (days = 90): Promise<PerformanceArchetype[]> => {
    const response = await apiClient.get<PerformanceArchetype[]>('/analytics/preferences/performance-archetypes', {
      params: { days, min_exposed_users: 25, limit: 20 },
    });
    return response.data;
  },
};
